<template>
  <ValidationObserver ref="observer" slim>
    <b-form class="col-12" :novalidate="true">
      <b-card>
        <b-form-row>
          <h4 class="col-12 text-primary font-weight-bold pb-2">
            Account Information
          </h4>
        </b-form-row>
        <div ref="accountErrors">
          <span
            v-for="error in accountErrors"
            :key="error.code"
            class="d-block invalid-feedback mb-3"
          >
            {{ error.description }}
          </span>
        </div>
        <b-form-row>
          <b-col lg="4">
            <ValidationProvider
              name="First Name"
              rules="required"
              v-slot="{ errors, classes }"
            >
              <b-form-group label-for="firstName">
                <template v-slot:label
                  ><span class="req-ind">*</span>First Name</template
                >
                <b-form-input
                  id="firstName"
                  v-model="account.firstName"
                  placeholder="Enter First Name"
                  maxlength="50"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'firstName')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="firstName" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col lg="4">
            <b-form-group label="Middle Name or Initial" label-for="middleName">
              <b-form-input
                id="middleName"
                name="middleName"
                v-model="account.middleName"
                placeholder="Enter Middle Name"
                maxlength="50"
              />
            </b-form-group>
          </b-col>
          <b-col lg="4">
            <ValidationProvider
              name="Last Name"
              rules="required"
              v-slot="{ errors, classes }"
            >
              <b-form-group label-for="lastName">
                <template v-slot:label
                  ><span class="req-ind">*</span>Last Name</template
                >
                <b-form-input
                  id="lastName"
                  name="lastName"
                  v-model="account.lastName"
                  placeholder="Enter Last Name"
                  maxlength="50"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'lastName')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="lastName" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isAdmin && isTCEOUser">
          <b-col lg="12">
            <ValidationProvider
              :name="`Town`"
              rules="required"
              v-slot="{ errors, classes }"
            >
              <b-form-group label="Towns" label-for="towns">
                <template v-slot:label>
                  <span class="req-ind">*</span>Towns
                </template>
                <multiselect
                  id="towns"
                  v-model="account.towns"
                  :options="townOptions"
                  :searchable="true"
                  :show-labels="false"
                  track-by="geocodeNum"
                  label="name"
                  :class="classes"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="false"
                  placeholder="Select Towns">
                </multiselect>
                <InputErrors :errors="errors" errorsIdPrefix="town" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isAdmin && isINFWBUser">
          <b-col lg="4">
            <ValidationProvider
              :name="`Regional Office`"
              rules="required"
              v-slot="{ errors, classes }"
            >
              <b-form-group label="Regional Office" label-for="regionalOfficeId">
                <template v-slot:label>
                  <span class="req-ind">*</span>Regional Office
                </template>
                <select
                    id="regionalOfficeId"
                    name="regionalOfficeId"
                    v-model="account.regionalOfficeId"
                    class="form-control"
                    :class="classes"
                  >
                  <option :value="null">Select a Regional Office</option>
                  <option v-for="option in regionalOfficeOptions" :key="option.id" :value="option.id">{{option.fullName}}</option>
                </select>
                <InputErrors :errors="errors" errorsIdPrefix="regionalOfficeId" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isAdmin && isMFSUser">
          <b-col lg="4">
            <ValidationProvider
              :name="`Call Number`"
              v-slot="{ classes }"
            >
              <b-form-group label="Call Number" label-for="forestProtectionUnitId">
                <select
                  id="forestProtectionUnitId"
                  name="forestProtectionUnitId"
                  v-model="account.forestProtectionUnitId"
                  class="form-control"
                  :class="classes"
                >
                  <option :value="null">Select a Call Number</option>
                  <option v-for="option in unitOptions" :key="option.id" :value="option.id">{{option.id}}</option>
                </select>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col lg="4">
            <b-form-group label="District">
              <div class="mt-2">{{district}}</div>
            </b-form-group>
          </b-col>
          <b-col lg="4">
            <b-form-group label="Region">
              <div class="mt-2">{{region}}</div>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col>
            <b-form-group label="Company Name (if applicable)" label-for="companyName">
              <b-form-input
                id="companyName"
                name="companyName"
                v-model="account.companyName"
                placeholder="Enter Company Name"
                maxlength="100"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col lg="4">
            <ValidationProvider
              name="Phone"
              :rules="{ required: '', phoneRegex: /^[0-9]{10}$/ }"
              v-slot="{ errors, classes }"
            >
              <div role="group" class="form-group">
                <label for="phone"> <span class="req-ind">*</span>Phone </label>
                <b-form-input
                  id="phone"
                  name="phone"
                  v-model="account.phoneNumber"
                  placeholder="Enter Phone Number"
                  type="tel"
                  maxlength="10"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'phone')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="phone" />
              </div>
            </ValidationProvider>
          </b-col>
          <b-col lg>
            <div role="group" class="form-group">
              <label for="isLicensedForester">
              Are you a Maine Licensed Forester?
              </label>
              <b-form-radio-group
                id="isLicensedForester"
                name="isLicensedForester"
                v-model="account.isLicensedForester"
                @change = "checkOption($event)"
              >
                <b-form-radio v-model="account.isLicensedForester" class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio v-model="account.isLicensedForester" class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
            </div>
            <ValidationProvider
              v-if="account.isLicensedForester==='true'"
              name="Licensed Forester Number"
              :rules="{ required: '', licenseRegex: /^(LF|FI)\d{1,10}$/ }"
              v-slot="{ errors, classes }"
            >
              <div role="group" class="form-group">
                <label for="licensedForesterId">
                <span class="req-ind">*</span>
                  Maine Forester's License Number
                </label>
                <b-form-input
                  id="licensedForesterId"
                  name="licensedForesterId"
                  v-model="account.licensedForesterId"
                  placeholder="Enter License Number"
                  maxlength="12"
                  :class="classes"
                  :aria-describedby="`licensedForesterInstruction ${$errorsIdString(errors, 'licensedForesterId')}`"
                />
                <InputErrors
                  :errors="errors"
                  errorsIdPrefix="licensedForesterId"
                />
                <small class="text-muted" id="licensedForesterInstruction">
                  If you are registering as a licensed forester, provide your
                  active, valid license number that has been issued by the Maine
                  Office of Professional and Occupational Regulation in
                  accordance with
                  <a
                    :href="links.title32Chapter76ForesterLicensing"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Title 32 Chapter 76 Forester Licensing</a
                  >. You cannot be indicated as a licensed forester on a FON
                  without providing this information as part of your account
                  profile.
                </small>
              </div>
            </ValidationProvider>
          </b-col>
        </b-form-row>
        <NewAccountInputs v-if="!edit" :isAdmin="isAdmin" :isGovEmail="govEmail" :account="account" />
        <DetailRow v-else label="Email" :value="account.email" />
      </b-card>
      <b-card>
        <AddressForm
          :isAdminEdit="isAdmin"
          :address="account.physicalAddress"
          addressType="Physical"
          addressSelector="physical"
        >
          <template v-slot:header>
            <div class="font-md col-md-12 mb-3">
              <strong>This is the physical address of where you live NOT the location of the activity.</strong>
            </div>
          </template>
        </AddressForm>
        <hr />
        <AddressForm
          :isAdminEdit="isAdmin"
          :disabled="account.mailingSameAsPhysical"
          :address="account.mailingAddress"
          addressType="Mailing"
          addressSelector="mailing"
        >
          <template v-slot:header>
            <b-form-checkbox
              id="mailingSameAsPhysical"
              v-model="account.mailingSameAsPhysical"
              name="mailingSameAsPhysical"
              @input="clearMailingAddress"
            >
              Same as Physical Address
            </b-form-checkbox>
          </template>
        </AddressForm>
      </b-card>
      <b-row align-h="around" class="my-3">
        <b-col v-if="allowCancel" md="5" lg="3" class="my-2">
          <b-button
            class="col"
            variant="danger"
            pill
            :to="{ name: cancelRouteName }"
            :disabled="submitting"
            aria-label="Cancel"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col md="5" lg="3" class="my-2">
          <b-button
            class="col"
            variant="primary"
            pill
            @click="submitWrapper"
            :disabled="submitting"
            aria-label="Submit"
          >
            {{ confirmLabel }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </ValidationObserver>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapState } from 'vuex';
import LookupModules from '@/store/moduleConfig';
import { initialAddress } from '@/helpers';
import AddressForm from '@/components/AddressForm.vue';
import DetailRow from '@/components/DetailRow.vue';
import FormSubmit from '@/mixins/FormSubmit';
import NewAccountInputs from '@/components/account/NewAccountInputs.vue';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'AccountForm',
  components: {
    AddressForm,
    DetailRow,
    NewAccountInputs,
    Multiselect,
  },
  mixins: [FormSubmit, Utilities],
  props: {
    initialAccount: {
      type: Object,
      required: true,
    },
    edit: {
      type: Boolean,
      required: true,
    },
    allowCancel: {
      type: Boolean,
      default: false,
    },
    cancelRouteName: {
      type: String,
      default: 'dashboard',
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: 'public',
    },
  },
  data() {
    let account = {
      firstName: '',
      middleName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
      phoneNumber: '',
      licensedForesterId: '',
      physicalAddress: {},
      mailingAddress: {},
      mailingSameAsPhysical: false,
      forestProtectionUnitId: null,
      regionalOfficeId: null,
      towns: [],
      townIds: [],
      isLicensedForester: '',
    };
    account = this.mergeTargetProps(account, this.initialAccount);
    if (this.edit) {
      account.isLicensedForester = (account.licensedForesterId !== '' && account.licensedForesterId != null) ? 'true' : 'false';
    }
    return {
      account,
      district: null,
      region: null,
      accountErrors: [],
      createRouteMap: {
        MFS: 'Admin/createMFSUser',
        TCEO: 'Admin/createTCEOUser',
        INFWB: 'Admin/createINFWBUser',
      },
      updateRouteMap: {
        MFS: 'Admin/updateMFSUser',
        TCEO: 'Admin/updateTCEOUser',
        INFWB: 'Admin/updateINFWBUser',
      },
    };
  },
  computed: {
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState(LookupModules.FOREST_PROTECTION_UNITS.namespace, {
      unitOptions: state => state.options,
    }),
    ...mapState(LookupModules.FOREST_PROTECTION_DISTRICTS.namespace, {
      districtOptions: state => state.options,
    }),
    ...mapState(LookupModules.REGIONS.namespace, {
      regionOptions: state => state.options,
    }),
    ...mapState(LookupModules.TOWNS.namespace, {
      townOptions: state => state.options.filter(t => [2, 3].includes(t.typeId) || t.name.toLowerCase().includes('plt')),
      townsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.REGIONALOFFICES.namespace, {
      regionalOfficeOptions: state => state.options,
    }),
    confirmLabel() {
      if (this.edit) {
        return this.isAdmin ? 'Update User' : 'Update My Profile';
      }

      return this.isAdmin ? `Create ${this.role} User` : 'Create Account';
    },
    govEmail() {
      return ['MFS', 'INFWB'].includes(this.role);
    },
    isMFSUser() {
      return this.role === 'MFS';
    },
    isTCEOUser() {
      return this.role === 'TCEO';
    },
    isINFWBUser() {
      return this.role === 'INFWB';
    },
  },
  watch: {
    // eslint-disable-next-line object-shorthand
    'account.forestProtectionUnitId'(fpuId) {
      this.checkFPU(fpuId);
    },
  },
  methods: {
    checkOption(e) {
      if (e === false) {
        this.account.licensedForesterId = '';
      } else if (e === true) {
        this.account.isLicensedForester = 'true';
      }
    },
    checkFPU(fpuId) {
      const fpu = this.unitOptions.find(u => u.id === fpuId) || {};
      let district = {};
      let region = {};
      if (fpu.forestProtectionDistrictId) {
        district = this.districtOptions.find(d => d.id === fpu.forestProtectionDistrictId) || {};

        if (district.regionId) {
          region = this.regionOptions.find(d => d.id === district.regionId) || {};
        }
      }
      this.district = district.name || 'N/A';
      this.region = region.name || 'N/A';
    },
    getScrollContainer() {
      return (
        document.querySelector('.form-container')
        || document.querySelector('#app')
      );
    },
    async submitWrapper() {
      const data = { ...this.account };
      if (data.mailingSameAsPhysical) {
        data.mailingAddress = data.physicalAddress;
      }

      data.townIds = data.towns.map(t => t.geocodeNum);
      data.towns = [];

      let url = 'User/create';
      if (this.edit) {
        if (this.isAdmin) {
          data.id = this.initialAccount.id;
        }
        url = this.isAdmin ? this.updateRouteMap[this.role] : 'User/update';
      } else if (this.isAdmin) {
        url = this.createRouteMap[this.role];
      }

      await this.submit(this.$refs.observer, url, data);
    },
    invalidError(firstEl) {
      this.$scrollTo(firstEl, {
        container: this.getScrollContainer(),
      });
    },
    clearMailingAddress() {
      this.account.mailingAddress = initialAddress();
    },
    submitSuccess() {
      this.$emit('submitted');
    },
    submitError(error) {
      this.accountErrors = error.response.data;
      this.$scrollTo(this.$refs.accountErrors, {
        container: this.getScrollContainer(),
      });
    },
  },
  async created() {
    if (this.isAdmin) {
      await this.$store.dispatch(`${LookupModules.FOREST_PROTECTION_UNITS.namespace}/fetchOptions`);
      await this.$store.dispatch(`${LookupModules.FOREST_PROTECTION_DISTRICTS.namespace}/fetchOptions`);
      await this.$store.dispatch(`${LookupModules.REGIONS.namespace}/fetchOptions`);
      await this.$store.dispatch(`${LookupModules.TOWNS.namespace}/fetchOptions`);
      await this.$store.dispatch(`${LookupModules.REGIONALOFFICES.namespace}/fetchOptions`);

      this.checkFPU(this.account.forestProtectionUnitId);
    }
  },
};
</script>

<style>
.multiselect__tags {
  display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
}

.multiselect__placeholder{
  color: #495057;
}

</style>
